// Explore-stats page: Question and answer length (#211).
// Q vs A character-length distribution from the `house_questions` index.
// Surfaces overall quartiles, per-portfolio percentiles (the supporting
// table), per-asker A-length leaderboard, "filibuster index" (median A
// length per sitting day), and 5 longest Qs / 5 longest As outliers.
//
// Recharts has no native box-plot, so the percentile view is a custom SVG
// box-and-whisker for the overall Q-vs-A pair, plus a Recharts stacked-bar
// approximation for the per-portfolio view (min/median/max as bars).

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatLine, PALETTE } from './charts';

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Lightweight SVG box-and-whisker for the Q-vs-A overall quartiles. Two
// boxes drawn side-by-side on a shared x-axis (character count).
function BoxAndWhisker({ qQuart, aQuart, height = 220 }) {
  if (!qQuart || !aQuart || !qQuart.max || !aQuart.max) {
    return <Typography variant="body2">No data.</Typography>;
  }
  const max = Math.max(qQuart.max, aQuart.max);
  const width = 720;
  const padL = 64;
  const padR = 24;
  const w = width - padL - padR;
  const scale = (v) => padL + (v / max) * w;
  const boxes = [
    { key: 'Question', y: 50, q: qQuart, color: PALETTE[1] },
    { key: 'Answer', y: 150, q: aQuart, color: PALETTE[0] },
  ];
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`}
         style={{ fontSize: 12, fontFamily: 'inherit' }}>
      <line x1={padL} y1={height - 30} x2={width - padR} y2={height - 30}
            stroke="#999" />
      {[0, 0.25, 0.5, 0.75, 1].map((p) => {
        const x = padL + p * w;
        const v = Math.round(p * max);
        return (
          <g key={p}>
            <line x1={x} y1={height - 30} x2={x} y2={height - 26}
                  stroke="#999" />
            <text x={x} y={height - 14} textAnchor="middle"
                  fill="currentColor">
              {v.toLocaleString()}
            </text>
          </g>
        );
      })}
      <text x={width / 2} y={height - 2} textAnchor="middle"
            fill="currentColor">characters</text>
      {boxes.map((b) => {
        const xQ1 = scale(b.q.q1);
        const xQ3 = scale(b.q.q3);
        const xMed = scale(b.q.median);
        const xP90 = scale(b.q.p90);
        const xMax = scale(b.q.max);
        const xMin = scale(0);
        return (
          <g key={b.key}>
            <text x={padL - 10} y={b.y + 20} textAnchor="end"
                  fill="currentColor">{b.key}</text>
            {/* whisker line */}
            <line x1={xMin} y1={b.y + 16} x2={xMax} y2={b.y + 16}
                  stroke={b.color} strokeWidth={1} />
            {/* box (q1..q3) */}
            <rect x={xQ1} y={b.y} width={xQ3 - xQ1} height={32}
                  fill={b.color} fillOpacity={0.3} stroke={b.color} />
            {/* median */}
            <line x1={xMed} y1={b.y} x2={xMed} y2={b.y + 32}
                  stroke={b.color} strokeWidth={2} />
            {/* p90 marker */}
            <line x1={xP90} y1={b.y + 4} x2={xP90} y2={b.y + 28}
                  stroke={b.color} strokeWidth={1} strokeDasharray="2 2" />
            {/* labels */}
            <text x={xMed} y={b.y - 4} textAnchor="middle" fill={b.color}>
              med {b.q.median.toLocaleString()}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function QtLength() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('qt-length');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const overall = c.overall_quartiles || {};
  const portfolioBarData = (c.by_portfolio || []).slice(0, 12).map((r) => ({
    portfolio: r.portfolio,
    'Median Q': r.q_median,
    'Median A': r.a_median,
  }));
  const askerBarData = (c.by_asker || []).slice(0, 10).map((r) => ({
    asker: r.asker,
    'Median A': r.a_median,
    'A 90th pct': r.a_p90,
  }));

  return (
    <ExploreStatsPage
      title="Question and answer length"
      description="Question vs answer character-count distribution across every captured QT question. Quartiles, per-portfolio medians, per-asker leaderboards, and a 'filibuster index' (median answer length per sitting day) to flag rhetorical drift."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="qt-length"
    >
      {data && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Median question"
                        value={overall.questions?.median
                          ? `${overall.questions.median.toLocaleString()}`
                          : '—'}
                        sub="characters" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Median answer"
                        value={overall.answers?.median
                          ? `${overall.answers.median.toLocaleString()}`
                          : '—'}
                        sub="characters" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Longest answer"
                        value={overall.answers?.max
                          ? `${overall.answers.max.toLocaleString()}`
                          : '—'}
                        sub="characters" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Longest question"
                        value={overall.questions?.max
                          ? `${overall.questions.max.toLocaleString()}`
                          : '—'}
                        sub="characters" />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Overall distribution — Q vs A
                </Typography>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}>
                  Box = Q1–Q3; solid line = median; dashed line = 90th
                  percentile; horizontal line = full range.
                </Typography>
                <BoxAndWhisker qQuart={overall.questions}
                               aQuart={overall.answers} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Median length by portfolio (chars)
                </Typography>
                <StatBar data={portfolioBarData} xKey="portfolio"
                         yKey="Median A" layout="vertical" height={400} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Askers who attract the longest answers
                </Typography>
                <StatBar data={askerBarData} xKey="asker"
                         yKey="A 90th pct" layout="vertical" height={400} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Filibuster index — median answer length per sitting day
                </Typography>
                <StatLine data={c.filibuster_index || []} xKey="date"
                          keys={['a_median']} height={300} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  5 longest questions
                </Typography>
                <Stack spacing={1}>
                  {(c.outliers_q || []).map((q, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2,
                                       borderBottom: '1px solid',
                                       borderColor: 'divider', pb: 1 }}>
                      <Box sx={{ minWidth: 100, fontVariantNumeric: 'tabular-nums' }}>
                        {q.Date}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {q.Asker} → {q.Responder}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                        {q.chars?.toLocaleString()} chars
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  5 longest answers
                </Typography>
                <Stack spacing={1}>
                  {(c.outliers_a || []).map((q, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2,
                                       borderBottom: '1px solid',
                                       borderColor: 'divider', pb: 1 }}>
                      <Box sx={{ minWidth: 100, fontVariantNumeric: 'tabular-nums' }}>
                        {q.Date}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {q.Asker} → {q.Responder}{' '}
                          <Typography component="span" variant="caption"
                                       color="text.secondary">
                            ({q.Portfolio})
                          </Typography>
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                        {q.chars?.toLocaleString()} chars
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
